import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { scheduleExpectedCountApi } from '../services/api';
import { ExpectedCountMode, ScheduleDayCounts, ScheduleExpectedCountWeek } from '../types';
import { EXPECTED_COUNT_DAY_LABELS, emptyDayCounts, normalizeDayCounts } from '../utils/scheduleExpectedCounts';
import { formatCompanyDate } from '../utils/companyDateFormat';

interface ScheduleExpectedCountsWeekModalProps {
  scheduleId: number;
  scheduleName: string;
  locationId: number;
  weekStart: string;
  weekEnd: string;
  onClose: () => void;
  onSaved: (week: ScheduleExpectedCountWeek) => void;
}

export default function ScheduleExpectedCountsWeekModal({
  scheduleId,
  scheduleName,
  locationId,
  weekStart,
  weekEnd,
  onClose,
  onSaved,
}: ScheduleExpectedCountsWeekModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<ExpectedCountMode>('Settings');
  const [settings, setSettings] = useState<ScheduleDayCounts>(emptyDayCounts());
  const [overrideCounts, setOverrideCounts] = useState<ScheduleDayCounts>(emptyDayCounts());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await scheduleExpectedCountApi.getWeek(scheduleId, { locationId, weekStart });
        if (cancelled) return;
        const settingsDays = normalizeDayCounts(data.settings);
        setSettings(settingsDays);
        setMode(data.mode === 'Override' ? 'Override' : 'Settings');
        setOverrideCounts(normalizeDayCounts(data.override || data.effective || settingsDays));
      } catch (error) {
        console.error('Error loading week expected counts:', error);
        if (!cancelled) alert('Failed to load expected counts for this week.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [scheduleId, locationId, weekStart]);

  const displayed = mode === 'Override' ? overrideCounts : settings;
  const inputsDisabled = mode !== 'Override';

  const handleModeChange = (next: ExpectedCountMode) => {
    if (next === 'Override' && mode === 'Settings') {
      setOverrideCounts(settings);
    }
    setMode(next);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await scheduleExpectedCountApi.saveWeek(scheduleId, {
        locationId,
        weekStart,
        mode,
        ...(mode === 'Override' ? overrideCounts : settings),
      });
      onSaved(saved);
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to save expected counts for this week';
      alert(typeof message === 'string' ? message : 'Failed to save expected counts for this week');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h3 className="text-lg font-semibold">Expected counts</h3>
            <p className="text-sm text-neutral-600 mt-1">
              {scheduleName} · {formatCompanyDate(weekStart)} – {formatCompanyDate(weekEnd)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-neutral-500 hover:text-neutral-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-neutral-600">Loading expected counts...</div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-neutral-800">
                <input
                  type="radio"
                  name="expected-count-mode"
                  checked={mode === 'Settings'}
                  onChange={() => handleModeChange('Settings')}
                />
                From Schedule Settings
              </label>
              <label className="flex items-center gap-2 text-sm text-neutral-800">
                <input
                  type="radio"
                  name="expected-count-mode"
                  checked={mode === 'Override'}
                  onChange={() => handleModeChange('Override')}
                />
                Override
              </label>
            </div>

            <p className="text-xs text-neutral-500">
              {mode === 'Settings'
                ? 'This week uses the default Monday–Sunday and BH values from Schedule Settings.'
                : 'Enter different counts for this week only. BH is used on public holidays instead of the weekday.'}
            </p>

            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-neutral-700">Day</th>
                    <th className="text-left p-3 text-sm font-medium text-neutral-700">Expected count</th>
                  </tr>
                </thead>
                <tbody>
                  {EXPECTED_COUNT_DAY_LABELS.map((day) => (
                    <tr key={day.key} className="border-b border-neutral-100">
                      <td className="p-3 text-sm font-medium">{day.label}</td>
                      <td className="p-3">
                        <input
                          type="number"
                          min={0}
                          disabled={inputsDisabled}
                          value={displayed[day.key]}
                          onChange={(e) =>
                            setOverrideCounts((prev) => ({
                              ...prev,
                              [day.key]: Math.max(0, Number(e.target.value) || 0),
                            }))
                          }
                          className="w-28 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100 disabled:text-neutral-600"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
